import { z } from 'zod';

import { useAppForm } from '@/app/lib/tanstack-form';
import { ProjectBillingTypeCode } from '@/app/network/models/project';
import { idBasedLookupSchema, IdBasedRef } from '@/app/network/models/refs';
import { sanitizeStringForApi } from '@/common/sanitization';

export const createUpdateProjectFormSchema = z.object({
  client: idBasedLookupSchema
    .nullable()
    .transform((val, ctx) => {
      if (val === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          fatal: true,
          message: 'Client is required'
        });

        return null;
      }

      return val! as IdBasedRef;
    }),
  name: z.string()
    .min(1, 'Name is required')
    .max(255, 'Name is too long'),
  budget: z.string()
    .optional(),
  startOn: z.string()
    .optional(),
  endOn: z.string()
    .optional(),
  rate: z.string()
    .min(1, 'Rate is required'),
  billingType: z.enum(['H', 'F', 'R', 'P'] as const, {
    required_error: 'Billing type is required',
    invalid_type_error: 'Please select a valid billing type'
  }) satisfies z.ZodEnum<[ProjectBillingTypeCode, ...ProjectBillingTypeCode[]]>
});

export type CreateUpdateProjectForm = z.infer<typeof createUpdateProjectFormSchema>;

// Default values with type inference for controlled inputs
export const createUpdateProjectFormDefaults = {
  client: null!,
  name: '',
  budget: '',
  startOn: '',
  endOn: '',
  rate: '',
  billingType: 'H' as const
} as CreateUpdateProjectForm;

type UseCreateUpdateProjectFormOptions = {
  defaultValues?: Partial<CreateUpdateProjectForm>;
  onSubmit: (values: CreateUpdateProjectForm) => Promise<void> | void;
};

export function useCreateUpdateProjectForm({ defaultValues, onSubmit }: UseCreateUpdateProjectFormOptions) {
  return useAppForm({
    defaultValues: { ...createUpdateProjectFormDefaults, ...defaultValues },
    validators: {
      onChange: createUpdateProjectFormSchema
    },
    onSubmit: async ({ formApi, value }) => {
      const sanitizedValues = {
        client: value.client,
        name: value.name,
        budget: sanitizeStringForApi(value.budget),
        startOn: sanitizeStringForApi(value.startOn),
        endOn: sanitizeStringForApi(value.endOn),
        rate: value.rate,
        billingType: value.billingType
      };

      await onSubmit(sanitizedValues);
      formApi.reset();
    }
  });
}
